'use client';
import { useEffect, useMemo } from 'react';
import { Outlet } from 'react-router';
import { ThemeProvider } from 'styled-components';

import type {
  ApiEnvironment,
  ApiUser,
} from './api/userEnvironment/types/ApiUserEnvironment';
import { AppConfigContextProvider } from './config/AppConfigContext';
import type { AppConfig } from './config/types/AppConfig';
import { ErrorBoundary } from './containers/ErrorBoundary';
import { ModalRoot } from './containers/Modal/ModalRoot';
import { TabContextProvider } from './containers/TabContext';
import { ToastsContainer } from './containers/ToastsContainer';
import { UserEnvironmentContextProvider } from './contexts/UserEnvironmentContext';
import { GlobalHandlers } from './global-handlers/GlobalHandlers';
import { I18nContext } from './i18n/contexts/I18nContext';
import { getI18n } from './i18n/getI18n';
import type { I18nLocale } from './i18n/types/I18nLocale';
import { AuthTmpStorageContextProvider } from './modules/auth/context/AuthTmpStorageContext';
import { SearchContextProvider } from './modules/search/contexts/SearchContext';
import { SavedSearchesContextProvider } from './modules/userDashboard/contexts/SavedSearchesContext';
import { themeContextValue } from './theme/theme';
import { deleteCookie } from './utils/cookies';
import { FLASHES_COOKIE } from './zustand-stores/flashesStore';
import type { Flash } from './zustand-stores/flashesStore';
import { HorizonFlashStoreProvider } from './zustand-stores/horizonFlashStore';

type Props = {
  appConfig: AppConfig;
  flash: Flash | null;
  locale: I18nLocale;
  user: ApiUser | null;
  environment: ApiEnvironment;
};

export function App({ appConfig, flash, locale, user, environment }: Props) {
  const localeContextValue = useMemo(() => getI18n(locale), [locale]);

  useEffect(() => {
    // Clear flash messages after showing it
    deleteCookie(FLASHES_COOKIE);
  }, []);

  return (
    <AppConfigContextProvider appConfig={appConfig}>
      <HorizonFlashStoreProvider initialState={{ flash }}>
        <I18nContext value={localeContextValue}>
          <UserEnvironmentContextProvider
            initialState={{
              user,
              environment,
              actionsWhileLoggedOutProcessedAt: null,
            }}
          >
            <ThemeProvider theme={themeContextValue}>
              <ErrorBoundary>
                <SearchContextProvider>
                  <GlobalHandlers />
                  <SavedSearchesContextProvider>
                    <AuthTmpStorageContextProvider>
                      <TabContextProvider>
                        <ModalRoot />
                        <ToastsContainer />
                        <Outlet />
                      </TabContextProvider>
                    </AuthTmpStorageContextProvider>
                  </SavedSearchesContextProvider>
                </SearchContextProvider>
              </ErrorBoundary>
            </ThemeProvider>
          </UserEnvironmentContextProvider>
        </I18nContext>
      </HorizonFlashStoreProvider>
    </AppConfigContextProvider>
  );
}
